import {
  CloudServerOutlined,
  FolderOpenOutlined,
  LockOutlined,
  LogoutOutlined,
  ShareAltOutlined,
  TeamOutlined,
  UserOutlined,
} from "@ant-design/icons";
import {
  Avatar,
  Button,
  Dropdown,
  Layout,
  Menu,
  Result,
  Spin,
  Tag,
  Typography,
} from "antd";
import { lazy, Suspense } from "react";
import { Redirect, Route, Switch, useLocation } from "wouter";
import { useAuth } from "./auth";
import { LoginPage } from "./pages/LoginPage";

const AdminUsersPage = lazy(() =>
  import("./pages/AdminUsersPage").then((module) => ({
    default: module.AdminUsersPage,
  })),
);
const ChangePasswordPage = lazy(() =>
  import("./pages/ChangePasswordPage").then((module) => ({
    default: module.ChangePasswordPage,
  })),
);
const FileManagerPage = lazy(() =>
  import("./pages/FileManagerPage").then((module) => ({
    default: module.FileManagerPage,
  })),
);
const PublicSharePage = lazy(() =>
  import("./pages/PublicSharePage").then((module) => ({
    default: module.PublicSharePage,
  })),
);
const ShareManagerPage = lazy(() =>
  import("./pages/ShareManagerPage").then((module) => ({
    default: module.ShareManagerPage,
  })),
);

const { Header, Sider, Content } = Layout;

export function App() {
  return (
    <Switch>
      <Route path="/share/:code">
        {(params) => (
          <Suspense
            fallback={
              <div className="full-screen-center">
                <Spin size="large" />
              </div>
            }
          >
            <PublicSharePage code={params.code} />
          </Suspense>
        )}
      </Route>
      <Route>
        <AuthenticatedGate />
      </Route>
    </Switch>
  );
}

function AuthenticatedGate() {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="full-screen-center">
        <Spin size="large" description="正在恢复登录状态…" />
      </div>
    );
  }
  if (!user) {
    return <LoginPage />;
  }
  return <AuthenticatedApp />;
}

function AuthenticatedApp() {
  const { user, logout } = useAuth();
  const [location, navigate] = useLocation();
  if (!user) {
    return null;
  }

  const isAdmin = user.role === "admin";
  const selectedKey = location.startsWith("/admin/users")
    ? "users"
    : location.startsWith("/shares") || location.includes("/shares")
      ? "shares"
    : location.startsWith("/settings/password")
      ? "password"
      : "files";

  return (
    <Layout className="app-layout">
      <Sider breakpoint="lg" collapsedWidth={0} className="app-sider">
        <div className="brand" onClick={() => navigate(isAdmin ? "/admin/users" : "/files")}>
          <CloudServerOutlined className="brand-icon" />
          <div>
            <Typography.Text strong className="brand-title">
              绿荫边缘存储
            </Typography.Text>
            <Typography.Text className="brand-subtitle">
              GSES · 社区内部存储
            </Typography.Text>
          </div>
        </div>
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[selectedKey]}
          items={
            isAdmin
              ? [
                  {
                    key: "users",
                    icon: <TeamOutlined />,
                    label: "用户管理",
                    onClick: () => navigate("/admin/users"),
                  },
                ]
              : [
                  {
                    key: "files",
                    icon: <FolderOpenOutlined />,
                    label: "我的文件",
                    onClick: () => navigate("/files"),
                  },
                  {
                    key: "shares",
                    icon: <ShareAltOutlined />,
                    label: "我的分享",
                    onClick: () => navigate("/shares"),
                  },
                  {
                    key: "password",
                    icon: <LockOutlined />,
                    label: "修改密码",
                    onClick: () => navigate("/settings/password"),
                  },
                ]
          }
        />
      </Sider>
      <Layout>
        <Header className="app-header">
          <div>
            <Typography.Title level={4} className="header-title">
              {isAdmin ? "管理控制台" : "个人文件空间"}
            </Typography.Title>
          </div>
          <Dropdown
            menu={{
              items: [
                {
                  key: "logout",
                  icon: <LogoutOutlined />,
                  label: "退出登录",
                  onClick: () => void logout(),
                },
              ],
            }}
          >
            <Button type="text" className="account-button">
              <Avatar size="small" icon={<UserOutlined />} />
              <span>{user.username}</span>
              <Tag color={isAdmin ? "gold" : user.permission === "read_write" ? "blue" : "default"}>
                {isAdmin ? "管理员" : user.permission === "read_write" ? "读写" : "只读"}
              </Tag>
            </Button>
          </Dropdown>
        </Header>
        <Content className="app-content">
          <Suspense
            fallback={
              <div className="route-loading">
                <Spin size="large" />
              </div>
            }
          >
            <Switch>
              {isAdmin ? (
                <>
                  <Route path="/admin/users" component={AdminUsersPage} />
                  <Route path="/admin/files/:userId/shares">
                    {(params) => (
                      <ShareManagerPage
                        adminMode
                        targetUserId={params.userId}
                      />
                    )}
                  </Route>
                  <Route path="/admin/files/:userId">
                    {(params) => (
                      <FileManagerPage
                        adminMode
                        targetUserId={params.userId}
                      />
                    )}
                  </Route>
                  <Route>
                    <Redirect to="/admin/users" replace />
                  </Route>
                </>
              ) : (
                <>
                  <Route path="/files">
                    <FileManagerPage />
                  </Route>
                  <Route path="/shares">
                    <ShareManagerPage />
                  </Route>
                  <Route
                    path="/settings/password"
                    component={ChangePasswordPage}
                  />
                  <Route>
                    <Redirect to="/files" replace />
                  </Route>
                </>
              )}
            </Switch>
          </Suspense>
        </Content>
      </Layout>
    </Layout>
  );
}

export function ErrorState({
  title = "加载失败",
  message,
  onRetry,
}: {
  title?: string;
  message: string;
  onRetry?: () => void;
}) {
  return (
    <Result
      status="error"
      title={title}
      subTitle={message}
      extra={
        onRetry ? (
          <Button type="primary" onClick={onRetry}>
            重试
          </Button>
        ) : undefined
      }
    />
  );
}
